import { Box, Chip, CircularProgress, IconButton, Link, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import Label from "../../../components/common/Label";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { useEffect, useMemo, useState, useCallback } from "react";
import CommentIcon from '@mui/icons-material/Comment';
import CommentHistory from "../commentHistory/CommentHistory";
import { extractFilenameFromKey, maskString } from "../../../util/util";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DataLabel from "./Fields/DataLabel";
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useRef } from "react";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import QuizIcon from '@mui/icons-material/Quiz';
import { getStatusColor } from "../../util";
const FieldDetails = (props) => {
  const {
    details,
    answer,
    handleDownloadSignature,
    answers,
    allAnswers,
    handleDownload,
    selectedfield,
    setSelectedField,
    userSiteTrialRole,
    formConfig,
    fieldQueries,
    hasOpenQueries: fieldHasOpenQueries,
    categoryKey,
    parentKey,
  } = props;
  // Category key for this field (from CategoryDetails); used to disambiguate shared fieldKeys
  const fieldCategoryKey = categoryKey || parentKey || null;
  const navigate = useNavigate()
  const theme = useTheme()
  const [searchParams, setSearchParams] = useSearchParams();
  const [sign, setSign] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureError, setSignatureError] = useState(false);
  const [signDate, setSignDate] = useState(null);
  const [signMode, setSignMode] = useState(null);
  const [signerName, setSignerName] = useState(null);
  const [critical, setCritical] = useState(false)
  const [openCommentHistory, setOpenCommentHistory] = useState(false);
  const VALID_FIELDS = ['INLINE_TEXT_FIELD', 'DROP_DOWN', 'RADIO_BUTTONS', 'SLIDER', 'COLOUR_SLIDER', 'PHONE_INPUT', 'YEAR_PICKER'];
  const MASK_FIELD_KEY = [
    'subjectName',
    'subjectNameKey',
    'subjectAddress',
    'subjectEmail',
    'subjectPhone',
    'subjectMobile',
    'accountNumber',
    'confirmAccountNumber',
    'routingNumber',
    'abaNumber',
    'swiftCode',
    'ssn',
    'firstName',
    'lastName',
    'selectGender',
    'dobBankDetails',
    'subjectEmail_BD',
    'subjectMobile_BD',
    'subjectAddress_BD',
    'subjectCity_BD',
    'subjectState_BD',
    'subjectZipcode_BD',
    'countrycode_BD',
    'governmentDocuments',
  ];
  const [isOverflowed, setIsOverflowed] = useState(false);
  const textRef = useRef(null);
  // Ref to track the last fetched signature key to prevent infinite loops
  const lastFetchedSignatureRef = useRef(null);
  
  // Compute query counts by status for this field
  const queryStatusCounts = useMemo(() => {
    if (!fieldQueries || fieldQueries.length === 0) return { OPEN: 0, RESOLVED: 0, VERIFIED: 0, REOPENED: 0, total: 0 };
    
    const counts = { OPEN: 0, RESOLVED: 0, VERIFIED: 0, REOPENED: 0, total: 0 };
    fieldQueries.forEach((query) => {
      if (counts.hasOwnProperty(query.status)) {
        counts[query.status]++;
      }
      counts.total++;
    });
    return counts;
  }, [fieldQueries]);

  // Compute open queries count for this field
  const openQueriesCount = useMemo(() => {
    return queryStatusCounts.OPEN + queryStatusCounts.REOPENED;
  }, [queryStatusCounts]);
  
  const hasQueries = fieldQueries && fieldQueries.length > 0;

  // Get status label for display
  const getStatusLabel = (status) => {
    switch (status) {
      case 'OPEN': return 'Open';
      case 'RESOLVED': return 'Resolved';
      case 'VERIFIED': return 'Verified';
      case 'REOPENED': return 'Reopened';
      default: return status;
    }
  };

  // Get icon color based on most critical status
  const getQueryIconColor = () => {
    if (queryStatusCounts.OPEN > 0 || queryStatusCounts.REOPENED > 0) {
      return getStatusColor('OPEN').text; // Red for open/reopened
    } else if (queryStatusCounts.RESOLVED > 0) {
      return getStatusColor('RESOLVED').text; // Green for resolved
    } else if (queryStatusCounts.VERIFIED > 0) {
      return getStatusColor('VERIFIED').text; // Blue for verified
    }
    return theme.palette.text.secondary;
  };

  // Render query status tooltip content
  const renderQueryTooltipContent = () => {
    return (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
          Queries ({queryStatusCounts.total})
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
          {['OPEN', 'REOPENED', 'RESOLVED', 'VERIFIED'].map((status) => (
            queryStatusCounts[status] > 0 && (
              <Chip
                key={status}
                label={`${getStatusLabel(status)}: ${queryStatusCounts[status]}`}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 500,
                  backgroundColor: getStatusColor(status).bg,
                  color: getStatusColor(status).text,
                  '& .MuiChip-label': {
                    paddingX: 0.75,
                  },
                }}
              />
            )
          ))}
        </Stack>
      </Box>
    );
  };
  // Helper function to check if signature value is valid for downloading
  const isValidSignatureValue = useCallback((value) => {
    if (value === null || value === undefined) return false;
    if (value === '') return false;
    if (value === 'testSign') return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    // Check for array with empty/null values
    if (Array.isArray(value)) {
      return value.length > 0 && value.some(v => v !== null && v !== undefined && v !== '' && v !== 'testSign');
    }
    return true;
  }, []);

  useEffect(() => {
    if (answers && details.type && details.type === 'SIGNATURE') {
      let signTime = null;
      if (details.currentDateKey) {
        signTime = props.parentKey
          ? answers?.[props.parentKey]?.[details.currentDateKey]
          : answers?.[details.currentDateKey];
      } else {
        signTime = answers?.[props.parentKey]?.[`${details.key}_sign_date`] ?? answers?.[`${details.key}_sign_date`];
      }
      if (signTime) {
        setSignDate(signTime);
      }
      
      // Get signature mode
      const mode = props.parentKey
        ? answers?.[props.parentKey]?.[`${details.key}_sign_mode`]
        : answers?.[`${details.key}_sign_mode`];
      if (mode) {
        setSignMode(mode);
      }
      
      // Get signer name
      const name = props.parentKey
        ? answers?.[props.parentKey]?.[`${details.key}_signer_name`]
        : answers?.[`${details.key}_signer_name`];
      if (name) {
        setSignerName(name);
      }
      
      // Only call API if signature answer exists, is valid, and hasn't been fetched yet
      if (isValidSignatureValue(answer) && lastFetchedSignatureRef.current !== answer) {
        // Mark this signature as being fetched to prevent duplicate calls
        lastFetchedSignatureRef.current = answer;
        setSignatureLoading(true);
        setSignatureError(false);
        
        // Use e-consent download API for all signatures (single source of truth)
        handleDownloadSignature(answer, (signData) => {
          if (signData) {
            setSign(signData);
          } else {
            setSignatureError(true);
          }
          setSignatureLoading(false);
        });
      }
    }
    if (details.criticalKey && details.criticalValue && allAnswers) {
      allAnswers[details.criticalKey] === details.criticalValue && setCritical(true)
    }
    // Note: handleDownloadSignature is intentionally excluded from deps to prevent infinite loops
    // The function is stable and only answer changes should trigger a re-fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, answers, answer, isValidSignatureValue, props.parentKey])
  const [isHovered, setIsHovered] = useState(false);


  useEffect(() => {
    // Open only when both fieldKey and categoryKey (when present) match this field
    const queryFieldKey = searchParams.get('fieldKey');
    const queryCategoryKey = searchParams.get('categoryKey');

    const fieldKeyMatches = queryFieldKey === details.key;
    const categoryMatches = fieldCategoryKey
      ? queryCategoryKey === fieldCategoryKey
      : !queryCategoryKey;

    if (queryFieldKey && fieldKeyMatches && categoryMatches) {
      setOpenCommentHistory(true);
      setSelectedField(details.key);
    } else if (
      !queryFieldKey &&
      selectedfield &&
      selectedfield === details.key &&
      !fieldCategoryKey
    ) {
      // Legacy selectedfield path for fields without a category
      setOpenCommentHistory(true);
    } else if (openCommentHistory && (!queryFieldKey || !fieldKeyMatches || !categoryMatches)) {
      // Close when URL no longer targets this field+category
      setOpenCommentHistory(false);
      if (!queryFieldKey) {
        setSelectedField(null);
      }
    }
  }, [selectedfield, details.key, fieldCategoryKey, searchParams, setSelectedField, openCommentHistory]);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        setIsOverflowed(textRef.current.scrollHeight > textRef.current.clientHeight);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [answer]);


  const handleMouseEnter = () => {
    if (userSiteTrialRole !== "subject") setIsHovered(true);
  };

  const handleMouseLeave = () => {
  if (userSiteTrialRole !== 'subject') setIsHovered(false);
  };
  const openHistory = () => {
    setIsHovered(false);
    setOpenCommentHistory(true);
    
    // Update URL query params — include categoryKey so only this field's drawer opens
    const params = new URLSearchParams(searchParams);
    params.set('fieldKey', details.key);
    if (fieldCategoryKey) {
      params.set('categoryKey', fieldCategoryKey);
    } else {
      params.delete('categoryKey');
    }
    // Don't set tab yet, let CommentHistory handle it
    setSearchParams(params, { replace: true });
  }
  const onCloseHistory = () => {
    setIsHovered(false);
    setOpenCommentHistory(false)
    setSelectedField(null);
    
    // Remove fieldKey, categoryKey and tab from URL
    const params = new URLSearchParams(searchParams);
    params.delete('fieldKey');
    params.delete('categoryKey');
    params.delete('tab');
    params.delete('queryId');
    setSearchParams(params, { replace: true });
  }
  return (
    <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {VALID_FIELDS.includes(details.type) && (
        <Box sx={{ display: 'flex', alignItems: 'start' }}>
          <Box sx={{ width: (details.tableRow || details.textFontWeight) && !details.label ? 0 : '50%' }}>
            {(details.tableRow || details.textFontWeight) ? (
              <Label sx={{ display: 'flex', alignItems: 'center' }}>
                {details.label
                  ? <>
                      {details.label}
                      {details.info && (
                        <Tooltip title={typeof details.info === 'object' ? JSON.stringify(details.info) : details.info}>
                          <InfoOutlinedIcon sx={{ color: '#0f52b9', mx: 1, fontSize: 18 }} />
                        </Tooltip>
                      )}
                    </>
                  : ''}
              </Label>
            ) : (
              <Label sx={{ display: 'flex', alignItems: 'center' }}>
                {details.label
                  ? <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {details.label}
                      {details.info && (
                        <Tooltip title={typeof details.info === 'object' ? JSON.stringify(details.info) : details.info}>
                          <InfoOutlinedIcon sx={{ color: '#0f52b9', mx: 1, fontSize: 18, }} />
                        </Tooltip>
                      )}
                    </Box>
                  : 'Additional Info'}
                :
              </Label>
            )}
          </Box>
          <Tooltip title={isOverflowed && answer}>
            <Typography
              variant="subtitle2"
              color="initial"
              ref={textRef}
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                wordBreak: 'break-word',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                whiteSpace: 'normal',
                fontWeight: critical ? 600 : 500,
                color: critical ? theme.palette.error.light : theme.palette.grey[900],
              }}
            >
              {MASK_FIELD_KEY.includes(details.key) && answer && userSiteTrialRole === 'site_monitor'
                ? maskString(answer)
                : answer}
              {details?.unit && <span style={{ fontStyle: 'italic', marginLeft: 8 }}>{details.unit}</span>}
            </Typography>
          </Tooltip>
          {/* Query indicator icon - always visible when field has queries */}
          {hasQueries && (
            <Tooltip 
              title={renderQueryTooltipContent()}
              arrow
              placement="top"
            >
              <IconButton
                onClick={openHistory}
                sx={{
                  width: '24px',
                  height: '24px',
                  color: getQueryIconColor(),
                }}
              >
                <QuizIcon sx={{ fontSize: 20 }}/>
              </IconButton>
            </Tooltip>
          )}
          {isHovered && (
            <IconButton
              color="primary"
              onClick={openHistory}
              sx={{
                width: '24px',
                height: '24px',
              }}
            >
              <CommentIcon sx={{ fontSize: 20 }}/>
            </IconButton>
          )}
        </Box>
      )}
      {(details.type == 'VIDEO_LINK' || details.type == 'DOCUMENT_LINK') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 1 }}>
          <Typography variant="h6">{details.label}</Typography>
          <Link href={details.link} target="_blank">
            {details.instructionText}
          </Link>
        </Box>
      )}
      {details.type == 'LABEL' && (
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ width: '100%' }}>
            <Label sx={{ fontWeight: (details.tableRow || details.textFontWeight) ? 500 : 600 }}>{details.label}</Label>
          </Box>
        </Box>
      )}
      {details.type == 'FILE_UPLOAD' && (
        <Box sx={{ display: 'flex', alignItems: 'start' }}>
          <Box sx={{ width: '50%' }}>
            <Label>{details.label ? details.label : 'Uploaded Doc'}:</Label>
          </Box>
          <Typography
            variant="subtitle2"
            color="initial"
            sx={{
              flexGrow: 1,
              fontWeight: critical ? 600 : 500,
              color: critical ? theme.palette.error.light : theme.palette.grey[900],
            }}
          >
            {userSiteTrialRole === 'site_monitor' && answer ? 'Uploaded' : extractFilenameFromKey(answer)}
          </Typography>
          {userSiteTrialRole !== 'site_monitor'
            ? answer && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', paddingLeft: 2, cursor: 'pointer' }}
                  onClick={() => handleDownload(answer, extractFilenameFromKey(answer))}
                >
                  <FileDownloadOutlinedIcon sx={{ color: '#3C6FEB', marginLeft: 1 }} />
                </Box>
              )
            : ''}
          {/* Query indicator icon - always visible when field has queries */}
          {hasQueries && (
            <Tooltip 
              title={renderQueryTooltipContent()}
              arrow
              placement="top"
            >
              <IconButton
                onClick={openHistory}
                sx={{
                  width: '24px',
                  height: '24px',
                  color: getQueryIconColor(),
                }}
              >
                <QuizIcon sx={{ fontSize: 20 }}/>
              </IconButton>
            </Tooltip>
          )}
          {isHovered && (
            <IconButton
              color="primary"
              onClick={openHistory}
              sx={{
                width: '24px',
                height: '24px',
              }}
            >
              <CommentIcon sx={{ fontSize: 20 }}/>
            </IconButton>
          )}
        </Box>
      )}
      {details.type == 'CHECK_BOX_GROUP' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
          {/* <Label>{details.label ? details.label : 'Additional Info'}:</Label> */}
          <Stack spacing={1}>
            {details?.options?.values.map((_option) => (
              <Box sx={{ display: 'flex', columnGap: 2 }}>
                {answer && answer.includes(_option) ? <CheckBoxIcon color="primary" /> : <CheckBoxOutlineBlankIcon />}
                <Typography>{_option}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
      {details.type == 'MULTISELECT_DROP_DOWN' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
          <Label>{details.label ? details.label : 'Additional Info'}:</Label>
          <Typography variant="body2">{Array.isArray(answer) ? answer.join(', ') : answer}</Typography>
        </Box>
      )}
      {details.type == 'CHECK_BOX' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', columnGap: 2 }}>
              {answer && (answer === '1' || answer === true) ? <CheckBoxIcon color="primary" /> : <CheckBoxOutlineBlankIcon />}
              <Typography>{details?.label}</Typography>
            </Box>
          </Stack>
        </Box>
      )}
      {details.type === 'DATA_LABEL' && (
        <Box sx={{ display: 'flex', width: '100%' }}>
          <Box sx={{ width: '50%' }}>
            <Label>{details.label ? details.label : 'Additional Info'}:</Label>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <DataLabel field={details} values={answers} userSiteTrialRole={userSiteTrialRole} />
          </Box>
          {/* Query indicator icon - always visible when field has queries */}
          {hasQueries && (
            <Tooltip 
              title={renderQueryTooltipContent()}
              arrow
              placement="top"
            >
              <IconButton
                onClick={openHistory}
                sx={{
                  width: '24px',
                  height: '24px',
                  color: getQueryIconColor(),
                }}
              >
                <QuizIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          {isHovered && (
            <IconButton
              color="primary"
              onClick={openHistory}
              sx={{
                width: '24px',
                height: '24px',
              }}
            >
              <CommentIcon sx={{ fontSize: 20 }}/>
            </IconButton>
          )}
        </Box>
      )}
      {details.type === 'SIGNATURE' && (
        <Box>
          {/* Role/Signer and Date display */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
            {details.role && <Typography variant="subtitle1">{details.role}</Typography>}
          </Box>
          {/* Field Label */}
          {details.label && (
            <Typography
              sx={{ color: '#5b5b5b', fontWeight: 'bold' }}
              variant="subtitle1"
              gutterBottom
            >
              {details.label}
            </Typography>
          )}
          {/* Secondary Label */}
          {details.label2 && (
            <Typography
              sx={{ color: '#5b5b5b', fontWeight: 'medium' }}
              variant="subtitle1"
              gutterBottom
            >
              {details.label2}
            </Typography>
          )}
          {/* <Box sx={{ display: 'flex' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, width: '50%' }}>
              Place:
            </Typography>
            {answers['']?.locationLatitude && answers['']?.locationLongitude && (
              <Tooltip
                title={`${answers && answers['']?.locationLatitude},${answers && answers['']?.locationLongitude}`}
              >
                <LocationOnOutlinedIcon sx={{ color: theme.palette.primary.main }} />
              </Tooltip>
            )}
          </Box> */}
          {signerName && (
            <Box sx={{ display: 'flex', marginY: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, width: '50%' }}>
                Signed by:
              </Typography>
              <Typography>{signerName}</Typography>
            </Box>
          )}
          {signDate && (
            <Box sx={{ display: 'flex', marginY: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, width: '50%' }}>
                Date:
              </Typography>
              <Typography>{signDate}</Typography>
            </Box>
          )}
          {/* {signMode && (
            <Box sx={{ display: 'flex', marginY: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, width: '50%' }}>
                Signature Type:
              </Typography>
              <Typography sx={{ textTransform: 'capitalize' }}>
                {signMode === 'draw' ? 'Drawn' : signMode === 'type' ? 'Typed' : signMode === 'upload' ? 'Uploaded' : signMode}
              </Typography>
            </Box>
          )} */}
          <Box sx={{ display: 'flex', border: '1px solid #808080', marginY: 2 }}>
            <Box
              sx={{
                height: 80,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
              }}
            >
              {signatureLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading signature...</Typography>
                </Box>
              ) : signatureError ? (
                <Typography sx={{ color: theme.palette.error.main }}>
                  Failed to load signature
                </Typography>
              ) : sign ? (
                <img src={sign} width={250} height={70} />
              ) : (
                <Typography sx={{ color: theme.palette.text.disabled }}>
                  Signature is not available
                </Typography>
              )}
            </Box>
            {/* Query indicator icon - always visible when field has queries */}
            {hasQueries && (
              <Tooltip 
                title={renderQueryTooltipContent()}
                arrow
                placement="top"
              >
                <IconButton
                  onClick={openHistory}
                  sx={{ color: getQueryIconColor() }}
                >
                  <QuizIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            {isHovered && (
              <IconButton color="primary" onClick={openHistory}>
                <CommentIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
      {openCommentHistory && (
        <CommentHistory
          {...props}
          field={details}
          answer={answer}
          onCloseHistory={onCloseHistory}
          open={openCommentHistory}
          categoryKey={fieldCategoryKey}
          MASK_FIELD_KEY={MASK_FIELD_KEY}
          userSiteTrialRole={userSiteTrialRole}
          formConfig={formConfig}
        />
      )}
    </Box>
  );
}

export default FieldDetails;